// Bottom-edge HUD hotbar — one-click access to all major panels.
// Tier-gated buttons (coven button hidden for non-maleficium, etc.).
// Keyboard shortcuts 1..9 + 0 mirror the button order.

import type { Net } from "../net.js";
import type { ClientStore, ClientState } from "../state.js";
import { getSettings, onSettings } from "./settings.js";

interface Slot {
  key: string;          // keyboard shortcut
  label: string;        // visible icon/glyph
  title: string;        // tooltip
  cmd: () => void;      // click action
  visible: (s: ClientState) => boolean;
}

export function mountHotbar(_uiHost: HTMLElement, net: Net, store: ClientStore): void {
  const bar = document.createElement("div");
  bar.className = "hotbar";
  bar.id = "hotbar";
  bar.style.display = "none";
  document.body.appendChild(bar);

  const slots: Slot[] = [
    { key: "1", label: "⚖", title: "Cases (/cases)",
      cmd: () => void import("./cases.js").then((m) => m.openCasesPanel(net, store)),
      visible: () => true },
    { key: "2", label: "✍", title: "Scribe's Workbench (/workbench)",
      cmd: () => void import("./casting.js").then((m) => m.openCastingPanel(net, store)),
      visible: () => true },
    { key: "3", label: "☽", title: "Rites & Fellowships (/rites)",
      cmd: () => void import("./coven_ritual.js").then((m) => m.openCovenRitualPanel(net, store)),
      visible: () => true },
    { key: "4", label: "✉", title: "Mail (/market)",
      cmd: () => void import("./economy.js").then((m) => m.openEconomyPanel(net, store, "mail")),
      visible: () => true },
    { key: "5", label: "⚒", title: "Auction (/market auction)",
      cmd: () => void import("./economy.js").then((m) => m.openEconomyPanel(net, store, "auction")),
      visible: () => true },
    { key: "6", label: "🏛", title: "Bank (/market bank)",
      cmd: () => void import("./economy.js").then((m) => m.openEconomyPanel(net, store, "bank")),
      visible: () => true },
    { key: "7", label: "🎒", title: "Inventory (/inv)",
      cmd: () => net.send({ t: "inventory" }),
      visible: () => true },
    { key: "8", label: "📜", title: "Tiers & Pledge (/tiers)",
      cmd: () => void import("./tiers.js").then((m) => m.renderTiersPanel(store)),
      visible: () => true },
    { key: "9", label: "⚜", title: "Standing (/standing)",
      cmd: () => net.send({ t: "standing" }),
      visible: () => true },
    { key: "0", label: "✟", title: "Inquisitor Rank (/rank)",
      cmd: () => net.send({ t: "inquisitor_status" }),
      visible: () => true },
  ];

  function render(s: ClientState): void {
    const cfg = getSettings();
    bar.style.display = s.phase === "playing" && cfg.show_hotbar ? "" : "none";
    if (s.phase !== "playing") return;
    if (!cfg.show_hotbar) return;
    const visible = slots.filter((sl) => sl.visible(s));
    bar.innerHTML = visible.map((sl, i) => `
      <button class="hotbar-btn" data-idx="${i}" title="${esc(sl.title)} (shortcut: ${esc(sl.key)})">
        <span class="hotbar-label">${esc(sl.label)}</span>
        <span class="hotbar-key">${esc(sl.key)}</span>
      </button>
    `).join("");
    for (const btn of bar.querySelectorAll<HTMLButtonElement>(".hotbar-btn")) {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.idx ?? 0);
        visible[idx]?.cmd();
      });
    }
  }

  store.subscribe(render);
  onSettings(() => render(store.get()));
  render(store.get());

  // Keyboard shortcuts (ignore when focus is on text input).
  document.addEventListener("keydown", (ev) => {
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
    const target = ev.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    const s = store.get();
    if (s.phase !== "playing") return;
    const hit = slots.find((sl) => sl.key === ev.key && sl.visible(s));
    if (hit) { ev.preventDefault(); hit.cmd(); }
  });
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}

export function ensureHotbarStyles(): void {
  if (document.getElementById("hotbar-styles")) return;
  const s = document.createElement("style");
  s.id = "hotbar-styles";
  s.textContent = `
    .hotbar { position: fixed; bottom: 10px; left: 50%; transform: translateX(-50%); z-index: 900; display: flex; gap: 6px; padding: 6px 10px; background: rgba(18, 21, 32, 0.92); border: 1px solid var(--line); border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.4); }
    .hotbar-btn { display: flex; flex-direction: column; align-items: center; gap: 2px; width: 46px; height: 46px; background: rgba(28, 32, 44, 0.8); border: 1px solid var(--line); border-radius: 6px; cursor: pointer; color: var(--txt); padding: 4px; }
    .hotbar-btn:hover { background: var(--panel); border-color: var(--gold); color: var(--gold); }
    .hotbar-label { font-size: 18px; line-height: 1; }
    .hotbar-key { font-size: 9px; color: var(--dim); font-family: ui-monospace, monospace; }
  `;
  document.head.appendChild(s);
}
